import { Typography, message } from 'antd';
import { Auth } from 'aws-amplify';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import shallow from 'zustand/shallow';

import { useAuthStore } from '../../stores';
import { FormPageProps, LocationState } from '../../typings';
import { getDefaultLocationState } from '../../utils';
import { CardPage } from '../components/CardPage';
import { RESET_PASSWORD_ROUTE } from '../routes';

import { SendNewPasswordCodeForm } from './components';

export function ForgotPassword({ defaultPath }: FormPageProps) {
  const { t } = useTranslation();
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { isAuthenticated } = useAuthStore(({ isAuthenticated }) => ({ isAuthenticated }), shallow);

  const success = React.useCallback(
    (email: string) => {
      message.success(t('VERIFICATION_CODE_SENT_SUCCESS'));
      history.push(`${RESET_PASSWORD_ROUTE}?email=${encodeURIComponent(email)}`);
    },
    [history, t]
  );

  const newPasswordCodeSubmit = React.useCallback(
    async (values) => {
      const { email } = values;

      try {
        await Auth.forgotPassword(email);

        success(email);
      } catch (error) {
        console.log('ForgotPassword -> error', error);

        // For security reasons handle unknow user as if user exists
        if (error.code === 'UserNotFoundException') {
          success(email);
        } else {
          message.error(error.message);
        }
      }
    },
    [success]
  );

  const [{ loading: newPasswordCodeLoading }, handleNewPasswordCodeSubmit] = useAsyncFn(newPasswordCodeSubmit);

  const { from } = location.state ?? getDefaultLocationState(defaultPath);

  if (isAuthenticated) return <Redirect to={from.pathname} />;

  return (
    <CardPage title={t('FORGOT_PASSWORD_TITLE')}>
      <Typography.Paragraph>
        {t('FORGOT_PASSWORD_PAGE_DESCRIPTION', { submit: t('SUBMIT_BUTTON') })}
      </Typography.Paragraph>

      <SendNewPasswordCodeForm onFinish={handleNewPasswordCodeSubmit} loading={newPasswordCodeLoading} />
    </CardPage>
  );
}
